import React, { useState, useEffect } from 'react';
import Icon from './icons/Icon';
import './EnhancedStatusBar.css';

/**
 * EnhancedStatusBar Component
 * 
 * A unified status bar that displays:
 * - Service running status
 * - Quick actions (Open App, View Logs, Restart)
 * - Port information
 * - Sync status
 * - Framework information (React, Next.js, or backend framework)
 * 
 * This component improves UX by providing clear status communication
 * and easy access to common actions without navigating multiple tabs.
 */
const EnhancedStatusBar = ({
  previewTabs = [],
  activePreviewTab = 0,
  activityLogs = [],
  onOpenApp,
  onViewLogs,
  onRestart,
  onViewSettings,
  onCaptureThumbnail, // New prop for thumbnail capture
  isCapturingThumbnail = false, // New prop for capture state
  syncStatus = null, // 'syncing', 'synced', 'error'
  lastSyncTime = null,
  logErrorCount = 0 // New prop for error count from logs
}) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Get active tab data
  const activeTab = previewTabs[activePreviewTab];
  
  if (!activeTab) {
    return (
      <div className="enhanced-status-bar empty">
        <div className="status-bar-section">
          <Icon name="info" size={16} />
          <span className="status-text">No services running</span>
        </div>
      </div>
    );
  }

  const isRunning = !!activeTab.url;
  const isLoading = activeTab.loading;
  const hasError = !!activeTab.error;

  // Format last sync time
  const getTimeSinceSync = () => {
    if (!lastSyncTime) return '';
    const seconds = Math.floor((currentTime - new Date(lastSyncTime)) / 1000);
    if (seconds < 5) return 'just now';
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    return 'over 1h ago';
  };

  // Get status indicator
  const getStatusIndicator = () => {
    if (hasError) return { icon: '🔴', text: 'Error', className: 'error' };
    if (isLoading) return { icon: '🟡', text: 'Starting', className: 'loading' };
    if (isRunning && syncStatus === 'syncing') return { icon: '🟡', text: 'Syncing', className: 'syncing' };
    if (isRunning) return { icon: '🟢', text: 'Running', className: 'running' };
    return { icon: '⚪', text: 'Stopped', className: 'stopped' };
  };

  const status = getStatusIndicator();

  return (
    <div className={`enhanced-status-bar ${status.className}`}>
      {/* Left Section: Status and Service Info */}
      <div 
        className="status-bar-section left clickable" 
        onClick={onViewLogs}
        title="Click to view logs"
      >
        <span className="status-indicator" title={status.text}>
          {status.icon}
        </span>
        <span className="service-name">{activeTab.serviceName}</span>
        <span className="status-text">{status.text}</span>
        
        {/* Error Count Badge */}
        {logErrorCount > 0 && (
          <span className="error-count-badge" title={`${logErrorCount} error${logErrorCount !== 1 ? 's' : ''} detected in logs`}>
            {'⚠️ '}{logErrorCount}{' error'}{logErrorCount !== 1 ? 's' : ''}
          </span>
        )}
        
        {/* Sync Status */}
        {syncStatus && (
          <span className={`sync-status ${syncStatus}`} title={`Sync status: ${syncStatus}`}>
            {syncStatus === 'syncing' && '↻ Syncing'}
            {syncStatus === 'synced' && '✓ Synced'}
            {syncStatus === 'error' && '⚠ Sync Error'}
            {lastSyncTime && ` (${getTimeSinceSync()})`}
          </span>
        )}
        
        {/* Activity Log - Show most recent activity */}
        {activityLogs.length > 0 && (
          <span className="activity-log" title="Recent activity">
            {activityLogs[activityLogs.length - 1].message}
          </span>
        )}
      </div>

      {/* Right Section: Quick Actions */}
      <div className="status-bar-section right">
        {hasError && (
          <span className="error-message" title={activeTab.error}>
            <Icon name="warning" size={14} />
            {activeTab.error?.substring(0, 50)}...
          </span>
        )}
        
        <div className="quick-actions">
          {isRunning && (
            <>
              <button
                className="status-action-btn"
                onClick={onCaptureThumbnail}
                disabled={isCapturingThumbnail}
                title={isCapturingThumbnail ? "Capturing thumbnail..." : "Capture thumbnail"}
                type="button"
              >
                {isCapturingThumbnail ? (
                  <span className="capture-spinner">⏳</span>
                ) : (
                  <Icon name="camera" size={14} />
                )}
              </button>
              
              <button
                className="status-action-btn primary"
                onClick={onOpenApp}
                title="Open application in new tab"
                type="button"
              >
                <Icon name="link" size={14} />
                Open App
              </button>
              
              <button
                className="status-action-btn"
                onClick={onViewLogs}
                title="View container logs"
                type="button"
              >
                <Icon name="list" size={14} />
                Logs
              </button>
            </>
          )}
        </div>

        {/* Framework Info */}
        {activeTab.framework && (
          <div className="framework-info" title={`Framework: ${activeTab.framework}`}>
            <Icon name="code" size={14} />
            {activeTab.framework}
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedStatusBar;
